/**
 * Chunk load error handler for Vite-based micro-frontends.
 *
 * When a new deployment occurs, older chunk URLs (e.g. App-uSFiT6RN.js) no longer
 * exist on the server/CDN. This handler detects chunk load failures and automatically
 * reloads the page once to fetch the latest asset manifest.
 */

const RELOAD_FLAG = 'chunk_reload_retry';
const RELOAD_PARAM = '__chunk_reload';

/**
 * Check if an error is a chunk load error (MIME type mismatch, missing chunks, etc.)
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = typeof err === 'string' ? err : (err as Error)?.message || String(err || '');
  const msgLower = msg.toLowerCase();
  return (
    msgLower.includes('failed to fetch dynamically imported module') ||
    msgLower.includes('error loading dynamically imported module') ||
    msgLower.includes('importing a module script failed') ||
    msgLower.includes('loading chunk') ||
    msgLower.includes('failed to load module script') ||
    msgLower.includes('expected a javascript-or-wasm module script') ||
    msgLower.includes('mime type') ||
    msgLower.includes("reading 'default'") ||
    msgLower.includes('reading default') ||
    msgLower.includes("property 'default'")
  );
}

/**
 * Trigger a page reload if not already retried (prevents infinite loops)
 */
export function reloadWithCacheBust(): void {
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Trigger a cache-busting page reload if not already retried.
 */
export function safeChunkReload(): boolean {
  const alreadyRetried = sessionStorage.getItem(RELOAD_FLAG);
  if (!alreadyRetried) {
    sessionStorage.setItem(RELOAD_FLAG, 'true');
    console.warn('[Platform] Chunk load failure detected. Auto-reloading page with cache bust...');
    reloadWithCacheBust();
    return true;
  }
  return false;
}

/**
 * Force a user-initiated chunk retry. Keeps the retry flag set so a persistent
 * missing chunk shows the recovery UI again instead of looping forever.
 */
export function forceChunkReload(): void {
  sessionStorage.setItem(RELOAD_FLAG, 'true');
  reloadWithCacheBust();
}

/**
 * Clear the reload flag after successful load
 */
function clearReloadFlag(): void {
  setTimeout(() => {
    sessionStorage.removeItem(RELOAD_FLAG);
    const url = new URL(window.location.href);
    if (url.searchParams.has(RELOAD_PARAM)) {
      url.searchParams.delete(RELOAD_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }, 3000);
}

/**
 * Set up chunk load error handlers for the current micro-frontend.
 * Call this early in your main.tsx entry point.
 */
export function setupChunkLoadErrorHandler(): void {
  // Vite-specific dynamic import preload failure event
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('[Vite] Dynamic import preload error detected.');
    event.preventDefault();
    safeChunkReload();
  });

  // Global unhandled promise rejections (e.g., React.lazy import failures)
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      console.warn('[Platform] Dynamic import failure in unhandledrejection:', event.reason);
      safeChunkReload();
    }
  });

  // Clear reload flag after successful load
  window.addEventListener('load', clearReloadFlag);
}
